import React, { useRef } from "react";
import html2canvas from "html2canvas";

/**
 * A component to display IQ score on a Gaussian (normal) distribution curve
 * @param {number} iqScore - The IQ score to display
 * @param {string} question - The question text to display
 */
const GaussianIqDisplay = ({ iqScore, question }) => {
  const componentRef = useRef(null);
  const width = 400; // Width of the graph area
  const height = 120; // Height of the graph area
  const padding = 30; // Padding around the graph area
  const topPadding = 60; // Extra padding on top for the smiley face
  const totalWidth = width + padding * 2;
  const totalHeight = height + padding + topPadding + 30; // Extra space for the site name // Adjusted to accommodate smiley

  // Calculate the IQ marker position with dynamic range
  const mean = 100;
  const stdDev = 15;
  
  // Dynamic range calculation - extend if IQ score is outside normal range
  let rangeMin = mean - 3 * stdDev; // Default: 55
  let rangeMax = mean + 3 * stdDev; // Default: 145
  
  // Extend range if IQ score is outside the default range
  if (iqScore < rangeMin) {
    rangeMin = Math.min(iqScore - 10, mean - 4 * stdDev); // Extend lower bound
  }
  if (iqScore > rangeMax) {
    rangeMax = Math.max(iqScore + 10, mean + 4 * stdDev); // Extend upper bound
  }

  // Generate the bell curve points with dynamic range
  const points = generateGaussianPoints(100, 15, width, height, rangeMin, rangeMax);

  // Create the SVG path data
  const pathData = points.reduce((acc, point, i) => {
    return `${acc}${i === 0 ? "M" : "L"}${point.x + padding},${
      point.y + topPadding
    }`;
  }, "");

  // Calculate percentile info
  const { positionDesc } = getPercentileInfo(iqScore);
  
  const markerX =
    ((iqScore - rangeMin) / (rangeMax - rangeMin)) * width + padding;

  // Dynamic marker positions based on the range
  const generateMarkers = (min, max) => {
    const markers = [];
    const step = 15; // Standard deviation step
    let current = Math.ceil(min / step) * step; // Start at next multiple of 15
    
    while (current <= max) {
      if (current >= min) {
        markers.push(current);
      }
      current += step;
    }
    
    // Ensure we always have key markers (85, 100, 115) if they're in range
    const keyMarkers = [85, 100, 115];
    keyMarkers.forEach(marker => {
      if (marker >= min && marker <= max && !markers.includes(marker)) {
        markers.push(marker);
      }
    });
    
    return markers.sort((a, b) => a - b);
  };
  
  const markers = generateMarkers(rangeMin, rangeMax);

  // Function to handle image download
  const handleDownload = async () => {
    if (componentRef.current) {
      try {
        // Wait a bit to ensure all rendering is complete
        await new Promise(resolve => setTimeout(resolve, 100));
        
        const canvas = await html2canvas(componentRef.current, {
          backgroundColor: "#FFFFFF", // Set background to white
          scale: 2, // Higher quality
          useCORS: true, // Enable cross-origin images if any
          logging: true, // For debugging
          windowHeight: componentRef.current.scrollHeight + 50,
          height: componentRef.current.scrollHeight + 50,
        });
        
        // Create a download link
        const link = document.createElement('a');
        link.download = 'provocabot.png';
        link.href = canvas.toDataURL('image/png');
        link.click();
      } catch (error) {
        console.error('Error generating image:', error);
      }
    }
  };

  return (
    <div className="rounded-lg bg-white p-6 shadow-md max-w-xl mx-auto">
      <div ref={componentRef}>
        {question && (
          <p className="text-center text-gray-700 italic mb-2 text-sm px-4">
            "{question}"
          </p>
        )}
        <h2 className="text-2xl font-bold mb-1 text-center">
          IQ Score: <span className="text-red-500">{iqScore}</span>
        </h2>
        <p className="text-center text-2xl font-semibold text-gray-700 mb-3">
          {positionDesc}
        </p>

        <svg width={totalWidth} height={totalHeight} className="mx-auto">
          {/* Blue bell curve */}
          <path d={pathData} fill="#60a5fa" stroke="none" />

          {/* White vertical dividers */}
          {markers.map((score, index) => {
            const x =
              ((score - rangeMin) / (rangeMax - rangeMin)) * width + padding;
            return (
              <line
                key={`divider-${index}`}
                x1={x}
                y1={topPadding}
                x2={x}
                y2={height + topPadding}
                stroke="white"
                strokeWidth="1"
              />
            );
          })}

          {/* Blue dotted line from x-axis to bottom of smiley */}
          <line
            x1={markerX}
            y1={height + topPadding}
            x2={markerX}
            y2={35} /* Stop before the smiley face */
            stroke="#0ea5e9"
            strokeDasharray="4,2"
            strokeWidth="2"
          />

          {/* IQ score indicators */}
          {markers.map((score, index) => {
            const x =
              ((score - rangeMin) / (rangeMax - rangeMin)) * width + padding;
            return (
              <g key={index}>
                <line
                  x1={x}
                  y1={height + topPadding}
                  x2={x}
                  y2={height + topPadding + 5}
                  stroke="#374151"
                  strokeWidth="1"
                />
                <text
                  x={x}
                  y={height + topPadding + 20}
                  textAnchor="middle"
                  fontSize="12"
                  fill={score === iqScore ? "#ef4444" : "#374151"}
                >
                  {score}
                </text>
              </g>
            );
          })}

          {/* Smiley face positioned higher with plenty of room */}
          <circle
            cx={markerX}
            cy={20}
            r={15}
            fill="none"
            stroke="#0ea5e9"
            strokeWidth="2"
          />
          <circle cx={markerX - 5} cy={15} r={1.5} fill="#0ea5e9" />
          <circle cx={markerX + 5} cy={15} r={1.5} fill="#0ea5e9" />
          <path
            d={`M${markerX - 5},${25} Q${markerX},${30} ${
              markerX + 5
            },${25}`}
            fill="none"
            stroke="#0ea5e9"
            strokeWidth="1.5"
          />
          
          {/* Add provocabot.com text directly in SVG */}
          <text
            x={totalWidth / 2}
            y={height + topPadding + 45}
            textAnchor="middle"
            fontSize="14"
            fontWeight="500"
            fill="#555555"
          >
            provocabot.com
          </text>
        </svg>
      </div>

      {/* Download button outside of componentRef */}
      <div className="mt-3">
        <button
          onClick={handleDownload}
          className="flex items-center px-3 py-1 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 transition-colors"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
          </svg>
          Download
        </button>
      </div>
    </div>
  );
};

// Generate points for the bell curve
const generateGaussianPoints = (
  mean = 100,
  stdDev = 15,
  width = 300,
  height = 120,
  rangeMin = null,
  rangeMax = null
) => {
  // Use provided range or default to 3 standard deviations
  const actualRangeMin = rangeMin !== null ? rangeMin : mean - 3 * stdDev;
  const actualRangeMax = rangeMax !== null ? rangeMax : mean + 3 * stdDev;
  
  // Standard normal distribution function
  const normalPdf = (x) => {
    return (
      (1 / (stdDev * Math.sqrt(2 * Math.PI))) *
      Math.exp(-0.5 * Math.pow((x - mean) / stdDev, 2))
    );
  };

  // Generate points along the x-axis
  const points = [];
  const step = (actualRangeMax - actualRangeMin) / 100; // 100 points for smooth curve

  // Max height of the normal PDF for normalization
  const maxHeight = normalPdf(mean);

  for (let x = actualRangeMin; x <= actualRangeMax; x += step) {
    const y = (normalPdf(x) / maxHeight) * height;
    const xPos = ((x - actualRangeMin) / (actualRangeMax - actualRangeMin)) * width;
    points.push({ x: xPos, y: height - y }); // Invert y for SVG coordinates
  }

  return points;
};

// Calculate percentile based on IQ score with dramatic range descriptions
const getPercentileInfo = (iq) => {
  const mean = 100;
  const stdDev = 15;

  // Calculate z-score
  const z = (iq - mean) / stdDev;

  // Calculate percentile using the normal distribution CDF approximation
  const percentile = Math.round(cdfNormal(z) * 100);

  // Determine the position description with dramatic naming
  let positionDesc;
  if (iq < 70) {
    positionDesc = "Retard (Bottom 2%)";
  } else if (iq >= 70 && iq < 85) {
    positionDesc = "Village Dunce (2-16%)";
  } else if (iq >= 85 && iq < 95) {
    positionDesc = "Basic Normie (16-37%)";
  } else if (iq >= 95 && iq < 105) {
    positionDesc = "Average Joe (37-63%)";
  } else if (iq >= 105 && iq < 115) {
    positionDesc = "Sharp Cookie (63-84%)";
  } else if (iq >= 115 && iq < 130) {
    positionDesc = "Clever Fox (84-98%)";
  } else if (iq >= 130 && iq < 145) {
    positionDesc = "Bright Genius (98-99.9%)";
  } else {
    positionDesc = "Einstein Territory (>99.9%)";
  }

  return { percentile, positionDesc };
};

// Normal distribution CDF approximation
const cdfNormal = (z) => {
  // Approximation of the normal CDF using polynomial
  if (z < -8.0) return 0.0;
  if (z > 8.0) return 1.0;

  let sum = 0.0,
    term = z;
  for (let i = 3; sum + term !== sum; i += 2) {
    sum += term;
    term = (term * z * z) / i;
  }

  return 0.5 + (sum * Math.exp((-z * z) / 2)) / Math.sqrt(2 * Math.PI);
};

export default GaussianIqDisplay;
